<template>
  <div
    ref="tceProductSliderBanner"
    :class="['tce-product-slider-banner', { 'tce-product-slider-banner--white': isTextColorWhite }]"
    :style="`background-color: ${backgroundColor}`"
  >
    <div
      :class="[
        'tce-product-slider-banner__slider',
        { 'tce-product-slider-banner__slider--single-slide': isSingleSlide },
      ]"
    >
      <h2 v-if="sliderTitle" class="tce-product-slider-banner__slider-title">
        {{ sliderTitle }}
      </h2>
      <div class="tce-product-slider-banner__wrapper" role="group" aria-roledescription="carousel">
        <ul
          ref="tceProductSliderList"
          class="tce-product-slider-banner__slides"
          role="presentation"
        >
          <slot name="list" />
        </ul>

        <div v-if="showControls" class="tce-product-slider-banner__controls">
          <button
            class="tce-product-slider-banner__prev"
            :disabled="isPrevDisabled"
            :aria-label="previousLabel"
            @click="prevSlide"
          ></button>
          <button
            class="tce-product-slider-banner__next"
            :disabled="isNextDisabled"
            :aria-label="nextLabel"
            @click="nextSlide"
          ></button>
        </div>
        <ul v-if="showControls" class="tce-product-slider-banner__dots">
          <li
            v-for="item in dotsAmount"
            :key="item - 1"
            class="tce-product-slider-banner__dot-wrapper"
          >
            <button
              :class="['tce-product-slider-banner__dot', { active: item - 1 === selectedDotId }]"
              :aria-current="item - 1 === selectedDotId"
              :aria-label="`${navigationDotLabel.startText} ${item} ${navigationDotLabel.middleText} ${dotsAmount}`"
              @click="selectedSlide(item - 1)"
            ></button>
          </li>
        </ul>
      </div>
    </div>
    <div class="tce-product-slider-banner__banner">
      <slot name="banner" />
    </div>
  </div>
</template>
<script>
export default {
  name: 'tce-product-slider-banner',
};
</script>

<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { debounce } from '../../utilities/timing';
import { AnalyticsHandler } from '../../directives/analyticsHandler';

const MOBILE_SCREEN_END = 767;
const MOBILE_CARD_PER_VIEW = 1;
const SLIDE_SELECTOR = '.tce-product-slider-banner__slide';
const VISIBLE = 'visible';
const HIDDEN = 'hidden';

const tceProductSliderBanner = ref(null);
const tceProductSliderList = ref(null);
const currentIndex = ref(0);
const selectedDotId = ref(0);
const visibleItems = ref();
const dotsAmount = ref();
let slidesAmount;

const props = defineProps({
  productCards: {
    type: [
      {
        productUrl: String,
        productImage: String,
        productName: String,
        price: String,
        shadesAmount: String,
      },
    ],
    default: () => [],
  },
  sliderTitle: {
    type: String,
    default: '',
  },
  backgroundColor: {
    type: String,
    default: '',
  },
  desktopCardPerView: {
    type: Number,
    default: 2,
  },
  isTextColorWhite: Boolean,
  previousLabel: { type: String, default: '' },
  nextLabel: { type: String, default: '' },
  navigationDotLabel: {
    type: {
      startText: String,
      middleText: String,
    },
    default: () => {},
  },
  analytics: {
    type: Object,
    default: () => {},
  },
});

onMounted(() => {
  slidesAmount = tceProductSliderList.value?.childElementCount;

  slidesLazyLoading();
  visibleItems.value = setVisibleItemsAmount.value;

  window.addEventListener('resize', debounce(onResize, 250));
});

const setVisibleItemsAmount = computed(() => {
  return isMobile.value ? MOBILE_CARD_PER_VIEW : props.desktopCardPerView;
});

const isMobile = computed(() => {
  return window.innerWidth < MOBILE_SCREEN_END;
});

const isPrevDisabled = computed(() => {
  return currentIndex.value === 0;
});

const isNextDisabled = computed(() => {
  return currentIndex.value + visibleItems.value >= slidesAmount;
});

const showControls = computed(() => {
  return slidesAmount > visibleItems.value;
});

const isSingleSlide = computed(() => {
  return props.desktopCardPerView === 1;
});

watch(
  () => visibleItems.value,
  () => {
    dotsAmount.value = Math.ceil(slidesAmount / visibleItems.value);
  },
);

function scrollSlideIntoView(nextItemId) {
  if (isMobile.value) {
    tceProductSliderList.value.scrollTo({
      left: tceProductSliderList.value.querySelectorAll(SLIDE_SELECTOR)[nextItemId].offsetLeft,
      behavior: 'smooth',
    });
  } else {
    tceProductSliderList.value.style = `transform: translateX(-${nextItemId * (100 / visibleItems.value)}%)`;
  }
}

function nextSlide() {
  const nextItemId = currentIndex.value + visibleItems.value;

  setCurrentSlideIndex(nextItemId, selectedDotId.value + 1);
  scrollSlideIntoView(currentIndex.value);

  pushAnalytics(props.nextLabel);
}

function prevSlide() {
  const visibleSlideId = currentIndex.value - visibleItems.value;
  currentIndex.value = visibleSlideId < 0 ? 0 : visibleSlideId;

  setCurrentSlideIndex(currentIndex.value, selectedDotId.value - 1);
  scrollSlideIntoView(currentIndex.value);

  pushAnalytics(props.previousLabel);
}

function selectedSlide(id) {
  const nextItemId = visibleItems.value * id;

  setCurrentSlideIndex(nextItemId, id);
  scrollSlideIntoView(currentIndex.value);
}

function setCurrentSlideIndex(nextItemId, dotId) {
  const amountOfVisibleSlides = slidesAmount - nextItemId;

  if (amountOfVisibleSlides <= visibleItems.value) {
    currentIndex.value = nextItemId - (visibleItems.value - amountOfVisibleSlides);
  } else {
    currentIndex.value = nextItemId;
  }

  selectedDotId.value = dotId;
}

function onResize() {
  if (visibleItems.value === setVisibleItemsAmount.value) return;
  visibleItems.value = setVisibleItemsAmount.value;
}

function slidesLazyLoading() {
  if (!tceProductSliderList.value) return;

  const slideItems = tceProductSliderList.value.querySelectorAll(SLIDE_SELECTOR);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const listItem = entry.target;
        const img = listItem.querySelector('img');

        if (entry.isIntersecting) {
          img.src = img.dataset.src;
          listItem.style.visibility = VISIBLE;

          if (isMobile.value) {
            listItem.setAttribute('visibility', VISIBLE);
          }
        } else {
          listItem.style.visibility = HIDDEN;

          if (isMobile.value) {
            listItem.setAttribute('visibility', HIDDEN);

            const visibleSlideIndex = Array.prototype.indexOf.call(
              tceProductSliderList.value.children,
              tceProductSliderList.value.querySelector(`[visibility=${VISIBLE}]`),
            );
            setCurrentSlideIndex(visibleSlideIndex, visibleSlideIndex);
          }
        }
      });
    },
    {
      root: tceProductSliderBanner.value,
      rootMargin: '-1px',
      threshold: 0.1,
    },
  );
  slideItems.forEach((item) => observer.observe(item));
}

function pushAnalytics(ctaName) {
  const tag = {
    ...props.analytics,
    type: 'userActionEvent',
    label: ctaName,
    cta_name: ctaName,
  };

  try {
    AnalyticsHandler.getAnalyticsHandler().push(tag);
  } catch (e) {
    console.warn('Could not push to dataLayer', tag);
  }
}
</script>

<style lang="scss">
@import '../../styles/style.scss';
@import './style.scss';
@import '../../components/productCard/style.scss';
</style>
